import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { Save, Eye, Send, ArrowLeft, Plus, Trash2, Edit2, Upload, Video, FileText, Image as ImageIcon, Link as LinkIcon, X } from 'lucide-react';

const TABS = [
  { id: 'general', label: 'Genel Bilgiler' },
  { id: 'sections', label: 'Bölümler & Dersler' },
  { id: 'materials', label: 'Materyaller' },
  { id: 'live', label: 'Canlı Dersler' },
  { id: 'publish', label: 'Görünürlük & Yayınlama' }
];

const YomevCourseEditor = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Genel',
    thumbnailUrl: '',
    targetAudience: [],
    visibilityScope: 'Public',
    allowedOrganizations: [],
    estimatedDuration: '',
    language: 'tr',
    courseType: 'Seçmeli',
    certificationPoints: 10
  });

  useEffect(() => {
    if (!user?.email?.endsWith('@yomev.org')) {
      navigate('/learning');
      return;
    }

    if (courseId && courseId !== 'new') {
      loadCourse();
    } else {
      setLoading(false);
    }
  }, [courseId, user, navigate]);

  const loadCourse = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${courseId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCourse(data);
        setFormData({
          title: data.title || '',
          description: data.description || '',
          category: data.category || 'Genel',
          thumbnailUrl: data.thumbnailUrl || '',
          targetAudience: data.targetAudience || [],
          visibilityScope: data.visibilityScope || 'Public',
          allowedOrganizations: data.allowedOrganizations || [],
          estimatedDuration: data.estimatedDuration || '',
          language: data.language || 'tr',
          courseType: data.courseType || 'Seçmeli',
          certificationPoints: data.certificationPoints || 10
        });
      }
    } catch (error) {
      console.error('Kurs yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (publish = false) => {
    if (!formData.title.trim()) {
      alert('Kurs adı zorunludur');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = courseId && courseId !== 'new'
        ? `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${courseId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses`;
      
      const method = courseId && courseId !== 'new' ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const savedCourse = await response.json();
        
        if (publish) {
          await handlePublish(savedCourse.id);
        } else {
          alert('Kurs başarıyla kaydedildi');
          if (courseId === 'new') {
            navigate(`/yomev/courses/${savedCourse.id}/edit`);
          }
        }
      } else {
        alert('Kurs kaydedilirken hata oluştu');
      }
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      alert('Kurs kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${id || courseId}/publish`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        alert('Kurs başarıyla yayına alındı! 🎉');
        navigate('/yomev/courses');
      } else {
        alert('Yayınlanırken hata oluştu');
      }
    } catch (error) {
      console.error('Yayınlama hatası:', error);
      alert('Yayınlanırken hata oluştu');
    }
  };

  const toggleAudience = (role) => {
    setFormData(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.includes(role)
        ? prev.targetAudience.filter(r => r !== role)
        : [...prev.targetAudience, role]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb 
        items={[
          { label: 'Online Eğitimlerim', path: '/yomev/courses' },
          { label: courseId === 'new' ? 'Yeni Kurs' : 'Kurs Düzenle', path: null }
        ]} 
      />

      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/yomev/courses')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Geri
        </button>
        
        <div className="flex gap-3">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Save size={18} />
            Taslağı Kaydet
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Send size={18} />
            Yayına Al
          </button>
        </div>
      </div>

      {/* Sekmeler */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'general' && (
            <GeneralInfoTab formData={formData} setFormData={setFormData} toggleAudience={toggleAudience} />
          )}
          {activeTab === 'sections' && (
            <SectionsTab courseId={courseId} />
          )}
          {activeTab === 'materials' && (
            <MaterialsTab courseId={courseId} />
          )}
          {activeTab === 'live' && (
            <LiveSessionsTab courseId={courseId} />
          )}
          {activeTab === 'publish' && (
            <PublishTab formData={formData} setFormData={setFormData} course={course} />
          )}
        </div>
      </div>
    </div>
  );
};

// Genel Bilgiler Sekmesi
const GeneralInfoTab = ({ formData, setFormData, toggleAudience }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Kurs Adı *
      </label>
      <input
        type="text"
        value={formData.title}
        onChange={(e) => setFormData({...formData, title: e.target.value})}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        placeholder="Örn: Okul Öncesi Eğitimde Gelişimsel Yaklaşım"
        required
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Kısa Açıklama *
      </label>
      <textarea
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
        rows={4}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        placeholder="Kursun içeriği hakkında kısa bir açıklama yazın..."
        required
      />
    </div>

    <div className="grid grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Kategori
        </label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({...formData, category: e.target.value})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="Genel">Genel</option>
          <option value="Pedagoji">Pedagoji</option>
          <option value="Gelişim">Gelişim</option>
          <option value="Sınıf Yönetimi">Sınıf Yönetimi</option>
          <option value="Aile İletişimi">Aile İletişimi</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tahmini Süre
        </label>
        <input
          type="text"
          value={formData.estimatedDuration}
          onChange={(e) => setFormData({...formData, estimatedDuration: e.target.value})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="Örn: 4 hafta, 8 oturum"
        />
      </div>
    </div>

    {/* Kurs Tipi ve Sertifika Puanı */}
    <div className="grid grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Kurs Tipi *
        </label>
        <select
          value={formData.courseType}
          onChange={(e) => setFormData({...formData, courseType: e.target.value})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="Zorunlu">🔴 Zorunlu (Mandatory)</option>
          <option value="Seçmeli">🔵 Seçmeli (Elective)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Zorunlu kurslar, öğretmen sertifikasyonu için tamamlanması gereken kurslardır
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sertifika Puanı
        </label>
        <input
          type="number"
          value={formData.certificationPoints}
          onChange={(e) => setFormData({...formData, certificationPoints: parseInt(e.target.value) || 0})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          min={0}
          max={100}
        />
        <p className="text-xs text-gray-500 mt-1">
          Bu kurs tamamlandığında kazanılacak sertifika puanı
        </p>
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Hedef Kitle * (Çoklu seçim)
      </label>
      <div className="grid grid-cols-2 gap-3">
        {[
          { value: 'TEACHER', label: 'Öğretmenler' },
          { value: 'COORDINATOR', label: 'Koordinatörler' },
          { value: 'ORG_OWNER', label: 'Yöneticiler' },
          { value: 'TRAINER', label: 'Eğitmenler' }
        ].map(item => (
          <label key={item.value} className="flex items-center gap-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.targetAudience.includes(item.value)}
              onChange={() => toggleAudience(item.value)}
              className="rounded text-blue-600"
            />
            <span>{item.label}</span>
          </label>
        ))}
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Kapak Görseli URL (Opsiyonel)
      </label>
      <input
        type="url"
        value={formData.thumbnailUrl}
        onChange={(e) => setFormData({...formData, thumbnailUrl: e.target.value})}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        placeholder="https://..."
      />
    </div>
  </div>
);

// Bölümler Sekmesi
const SectionsTab = ({ courseId }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showLessonDetailModal, setShowLessonDetailModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [newSection, setNewSection] = useState({ title: '', description: '', orderIndex: 0 });
  const [newLesson, setNewLesson] = useState({ 
    title: '', 
    description: '', 
    lessonType: 'Kayıttan', 
    orderIndex: 0, 
    videoUrl: '',
    content: ''
  });
  const [savingLesson, setSavingLesson] = useState(false);

  useEffect(() => {
    if (courseId && courseId !== 'new') {
      loadSections();
    } else {
      setLoading(false);
    }
  }, [courseId]);

  const loadSections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${courseId}/sections`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSections(data);
      }
    } catch (error) {
      console.error('Bölümler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSection = async () => {
    if (!newSection.title.trim()) {
      alert('Bölüm adı zorunludur');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${courseId}/sections`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...newSection, courseId })
        }
      );
      
      if (response.ok) {
        setShowSectionModal(false);
        setNewSection({ title: '', description: '', orderIndex: 0 });
        loadSections();
      } else {
        const err = await response.json();
        alert('Hata: ' + (err.detail || 'Bölüm oluşturulamadı'));
      }
    } catch (error) {
      console.error('Bölüm oluşturma hatası:', error);
      alert('Bölüm oluşturulurken hata oluştu');
    }
  };

  const createLesson = async () => {
    if (!newLesson.title.trim()) {
      alert('Ders adı zorunludur');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${courseId}/sections/${currentSection.id}/lessons`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            ...newLesson, 
            courseId,
            sectionId: currentSection.id 
          })
        }
      );
      
      if (response.ok) {
        setShowLessonModal(false);
        setNewLesson({ title: '', description: '', lessonType: 'Kayıttan', orderIndex: 0, videoUrl: '', content: '' });
        loadSections();
      } else {
        const err = await response.json();
        alert('Hata: ' + (err.detail || 'Ders oluşturulamadı'));
      }
    } catch (error) {
      console.error('Ders oluşturma hatası:', error);
      alert('Ders oluşturulurken hata oluştu');
    }
  };

  const openLessonDetail = (lesson, section) => {
    setCurrentLesson(lesson);
    setCurrentSection(section);
    setShowLessonDetailModal(true);
  };

  const updateLesson = async (updatedData) => {
    setSavingLesson(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/lessons/${currentLesson.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...currentLesson,
            ...updatedData,
            courseId,
            sectionId: currentSection.id
          })
        }
      );
      
      if (response.ok) {
        loadSections();
        return true;
      } else {
        const err = await response.json();
        alert('Hata: ' + (err.detail || 'Ders güncellenemedi'));
        return false;
      }
    } catch (error) {
      console.error('Ders güncelleme hatası:', error);
      alert('Ders güncellenirken hata oluştu');
      return false;
    } finally {
      setSavingLesson(false);
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm('Bu dersi silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/lessons/${lessonId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        loadSections();
        setShowLessonDetailModal(false);
      }
    } catch (error) {
      console.error('Ders silme hatası:', error);
    }
  };

  if (courseId === 'new') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Önce kursu kaydedin, sonra bölüm ve ders ekleyebilirsiniz.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-12">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Bölümler ve Dersler</h3>
        <button
          onClick={() => setShowSectionModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={18} />
          Yeni Bölüm
        </button>
      </div>

      {sections.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">Henüz bölüm eklenmemiş. Başlamak için "Yeni Bölüm" butonuna tıklayın.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.id} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-semibold text-lg">{section.title}</h4>
                  {section.description && <p className="text-sm text-gray-600 mt-1">{section.description}</p>}
                </div>
                <button
                  onClick={() => { setCurrentSection(section); setShowLessonModal(true); }}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-white border rounded hover:bg-gray-100"
                >
                  <Plus size={16} />
                  Ders Ekle
                </button>
              </div>
              
              {section.lessons && section.lessons.length > 0 ? (
                <div className="mt-3 space-y-2 pl-4 border-l-2 border-blue-300">
                  {section.lessons.map((lesson) => (
                    <div 
                      key={lesson.id} 
                      className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-blue-300 cursor-pointer transition-colors"
                      onClick={() => openLessonDetail(lesson, section)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{lesson.lessonType === 'Canlı' ? '🔴' : '📹'}</span>
                        <div>
                          <span className="font-medium text-gray-900">{lesson.title}</span>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {lesson.lessonType === 'Canlı' ? 'Canlı Ders' : 'Kayıttan İzle'}
                            {lesson.duration && ` • ${lesson.duration} dk`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Edit2 size={16} className="text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-2 pl-4">Bu bölümde henüz ders yok</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bölüm Ekleme Modal */}
      {showSectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Yeni Bölüm Ekle</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Bölüm Adı *</label>
                <input
                  type="text"
                  value={newSection.title}
                  onChange={(e) => setNewSection({...newSection, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Örn: Giriş ve Temel Kavramlar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Açıklama</label>
                <textarea
                  value={newSection.description}
                  onChange={(e) => setNewSection({...newSection, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Bölüm hakkında kısa açıklama"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowSectionModal(false); setNewSection({ title: '', description: '', orderIndex: 0 }); }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={createSection}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ders Ekleme Modal */}
      {showLessonModal && currentSection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">"{currentSection.title}" - Yeni Ders Ekle</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Ders Adı *</label>
                <input
                  type="text"
                  value={newLesson.title}
                  onChange={(e) => setNewLesson({...newLesson, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Örn: Video 1 - Temel Prensipler"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ders Tipi</label>
                <select
                  value={newLesson.lessonType}
                  onChange={(e) => setNewLesson({...newLesson, lessonType: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="Kayıttan">Asenkron (Video/Materyal)</option>
                  <option value="Canlı">Canlı Ders</option>
                </select>
              </div>
              {newLesson.lessonType === 'Kayıttan' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Video URL (Opsiyonel)</label>
                  <input
                    type="url"
                    value={newLesson.videoUrl}
                    onChange={(e) => setNewLesson({...newLesson, videoUrl: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://youtube.com/... veya https://vimeo.com/..."
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Açıklama</label>
                <textarea
                  value={newLesson.description}
                  onChange={(e) => setNewLesson({...newLesson, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Ders hakkında kısa açıklama"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ders İçeriği (Metin)</label>
                <textarea
                  value={newLesson.content}
                  onChange={(e) => setNewLesson({...newLesson, content: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={4}
                  placeholder="Ders içeriği, notlar, önemli noktalar..."
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowLessonModal(false); setNewLesson({ title: '', description: '', lessonType: 'Kayıttan', orderIndex: 0, videoUrl: '', content: '' }); }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={createLesson}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ders Detay/Düzenleme Modal */}
      {showLessonDetailModal && currentLesson && (
        <LessonDetailModal
          lesson={currentLesson}
          courseId={courseId}
          onClose={() => { setShowLessonDetailModal(false); setCurrentLesson(null); }}
          onUpdate={updateLesson}
          onDelete={() => deleteLesson(currentLesson.id)}
          saving={savingLesson}
        />
      )}
    </div>
  );
};

// Ders Detay Modal Bileşeni
const LessonDetailModal = ({ lesson, courseId, onClose, onUpdate, onDelete, saving }) => {
  const [activeTab, setActiveTab] = useState('content');
  const [formData, setFormData] = useState({
    title: lesson.title || '',
    description: lesson.description || '',
    lessonType: lesson.lessonType || 'Kayıttan',
    videoUrl: lesson.videoUrl || '',
    content: lesson.content || '',
    duration: lesson.duration || '',
    instructorId: lesson.instructorId || ''
  });
  const [resources, setResources] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loadingResources, setLoadingResources] = useState(true);
  const [loadingRecordings, setLoadingRecordings] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    loadResources();
    loadRecordings();
    loadInstructors();
  }, [lesson.id]);

  const loadResources = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/lessons/${lesson.id}/resources`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      console.error('Kaynaklar yüklenemedi:', error);
    } finally {
      setLoadingResources(false);
    }
  };

  const loadRecordings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/lessons/${lesson.id}/recordings`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setRecordings(data);
      }
    } catch (error) {
      console.error('Kayıtlar yüklenemedi:', error);
    } finally {
      setLoadingRecordings(false);
    }
  };

  const loadInstructors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${courseId}/instructors`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setInstructors(data);
      }
    } catch (error) {
      console.error('Eğitmenler yüklenemedi:', error);
    }
  };

  // Quiz/Assessment state and functions
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(true);
  const [showQuizEditor, setShowQuizEditor] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);

  const loadQuizzes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/lessons/${lesson.id}/quizzes`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setQuizzes(data);
      }
    } catch (error) {
      console.error('Quizler yüklenemedi:', error);
    } finally {
      setLoadingQuizzes(false);
    }
  };

  const createQuiz = async () => {
    const title = prompt('Quiz başlığı girin:');
    if (!title) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/lessons/${lesson.id}/quizzes`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title,
            lessonId: lesson.id,
            courseId: courseId
          })
        }
      );
      if (response.ok) {
        const newQuiz = await response.json();
        setQuizzes([...quizzes, newQuiz]);
        setCurrentQuiz(newQuiz);
        setShowQuizEditor(true);
      }
    } catch (error) {
      console.error('Quiz oluşturulamadı:', error);
    }
  };

  const deleteQuiz = async (quizId) => {
    if (!window.confirm('Bu quiz\'i silmek istediğinize emin misiniz?')) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/quizzes/${quizId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setQuizzes(quizzes.filter(q => q.id !== quizId));
    } catch (error) {
      console.error('Quiz silinemedi:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'assessment') {
      loadQuizzes();
    }
  }, [activeTab]);

  const handleSave = async () => {
    const success = await onUpdate(formData);
    if (success) {
      alert('Ders güncellendi!');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      
      // Önce S3'e yükle
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const uploadResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${courseId}/upload`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload
        }
      );
      
      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        
        // Sonra resource olarak kaydet
        const resourceData = {
          lessonId: lesson.id,
          courseId: courseId,
          title: file.name,
          resourceType: getResourceType(file.name),
          fileUrl: uploadData.url,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type
        };
        
        const resourceResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/yomev/lessons/${lesson.id}/resources`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(resourceData)
          }
        );
        
        if (resourceResponse.ok) {
          loadResources();
        }
      } else {
        alert('Dosya yüklenemedi');
      }
    } catch (error) {
      console.error('Dosya yükleme hatası:', error);
      alert('Dosya yüklenirken hata oluştu');
    } finally {
      setUploadingFile(false);
    }
  };

  const getResourceType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const types = {
      pdf: 'PDF', doc: 'Word', docx: 'Word', 
      ppt: 'PowerPoint', pptx: 'PowerPoint',
      mp4: 'Video', mov: 'Video', avi: 'Video',
      mp3: 'Video', wav: 'Video'
    };
    return types[ext] || 'Diğer';
  };

  const deleteResource = async (resourceId) => {
    if (!window.confirm('Bu kaynağı silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/resources/${resourceId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        loadResources();
      }
    } catch (error) {
      console.error('Kaynak silme hatası:', error);
    }
  };

  const addRecording = async (url, title) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/lessons/${lesson.id}/recordings`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ recordingUrl: url, title })
        }
      );
      
      if (response.ok) {
        loadRecordings();
      } else {
        alert('Kayıt eklenemedi');
      }
    } catch (error) {
      console.error('Kayıt ekleme hatası:', error);
    }
  };

  const deleteRecording = async (recordingId) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/yomev/recordings/${recordingId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        loadRecordings();
      }
    } catch (error) {
      console.error('Kayıt silme hatası:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{lesson.title}</h2>
            <p className="text-sm text-gray-500">{lesson.lessonType === 'Canlı' ? '🔴 Canlı Ders' : '📹 Kayıttan İzle'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {[
              { id: 'content', label: '📝 İçerik' },
              { id: 'instructor', label: '👨‍🏫 Eğitmen' },
              { id: 'resources', label: '📎 Dosyalar' },
              { id: 'recordings', label: '🎬 Kayıtlar' },
              { id: 'assessment', label: '📊 Ölçme' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'content' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Ders Adı</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Ders Tipi</label>
                  <select
                    value={formData.lessonType}
                    onChange={(e) => setFormData({...formData, lessonType: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Kayıttan">Asenkron</option>
                    <option value="Canlı">Canlı Ders</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Süre (dk)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value) || ''})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Örn: 45"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Video URL</label>
                <input
                  type="url"
                  value={formData.videoUrl}
                  onChange={(e) => setFormData({...formData, videoUrl: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://youtube.com/... veya https://vimeo.com/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ders İçeriği / Notlar</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={6}
                  placeholder="Ders içeriği, önemli noktalar, notlar..."
                />
              </div>
            </div>
          )}

          {activeTab === 'resources' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Ders Dosyaları</h3>
                <label className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer flex items-center gap-2">
                  <Upload size={18} />
                  {uploadingFile ? 'Yükleniyor...' : 'Dosya Ekle'}
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.mp4,.mp3,.jpg,.jpeg,.png"
                  />
                </label>
              </div>

              {loadingResources ? (
                <p className="text-gray-500 text-center py-4">Yükleniyor...</p>
              ) : resources.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <FileText size={40} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">Henüz dosya eklenmemiş</p>
                  <p className="text-sm text-gray-400">PDF, Word, PowerPoint, Video dosyaları ekleyebilirsiniz</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {resources.map(resource => (
                    <div key={resource.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {resource.resourceType === 'PDF' ? '📄' : 
                           resource.resourceType === 'Word' ? '📝' :
                           resource.resourceType === 'PowerPoint' ? '📊' :
                           resource.resourceType === 'Video' ? '🎬' : '📎'}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">{resource.title}</p>
                          <p className="text-xs text-gray-500">
                            {resource.resourceType}
                            {resource.fileSize && ` • ${(resource.fileSize / 1024 / 1024).toFixed(1)} MB`}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a 
                          href={resource.fileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Eye size={18} />
                        </a>
                        <button 
                          onClick={() => deleteResource(resource.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'instructor' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Ders Eğitmeni</label>
                <select
                  value={formData.instructorId}
                  onChange={(e) => setFormData({...formData, instructorId: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Seçiniz...</option>
                  {instructors.map(inst => (
                    <option key={inst.user.id} value={inst.user.id}>
                      {inst.user.fullName || inst.user.email} {inst.user.title && `(${inst.user.title})`}
                    </option>
                  ))}
                </select>
              </div>

              {formData.instructorId && instructors.find(i => i.user.id === formData.instructorId) && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  {(() => {
                    const instructor = instructors.find(i => i.user.id === formData.instructorId)?.user;
                    return instructor ? (
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-2xl">
                          {instructor.profileImageUrl ? (
                            <img src={instructor.profileImageUrl} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            '👨‍🏫'
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{instructor.fullName || instructor.email}</h4>
                          {instructor.title && (
                            <p className="text-sm text-blue-600">{instructor.title}</p>
                          )}
                          {instructor.bio && (
                            <p className="text-sm text-gray-600 mt-2">{instructor.bio}</p>
                          )}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {instructors.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Kursa henüz eğitmen atanmamış</p>
                  <p className="text-sm text-gray-400 mt-1">Kurs ayarlarından eğitmen ekleyebilirsiniz</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'recordings' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Canlı Ders Kayıtları</h3>
                <button
                  onClick={() => {
                    const url = prompt('Kayıt URL\'si girin:');
                    if (url) {
                      const title = prompt('Kayıt başlığı:', 'Canlı Ders Kaydı');
                      addRecording(url, title || 'Canlı Ders Kaydı');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={18} />
                  Kayıt Ekle
                </button>
              </div>

              {loadingRecordings ? (
                <p className="text-gray-500 text-center py-4">Yükleniyor...</p>
              ) : recordings.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Video size={40} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">Henüz kayıt eklenmemiş</p>
                  <p className="text-sm text-gray-400">Canlı ders kayıtlarını buradan ekleyebilirsiniz</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recordings.map(recording => (
                    <div key={recording.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🎬</span>
                        <div>
                          <p className="font-medium text-gray-900">{recording.title}</p>
                          <p className="text-xs text-gray-500">
                            {recording.duration && `${recording.duration} dk`}
                            {recording.recordedAt && ` • ${new Date(recording.recordedAt).toLocaleDateString('tr-TR')}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a 
                          href={recording.recordingUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Eye size={18} />
                        </a>
                        <button 
                          onClick={() => deleteRecording(recording.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'assessment' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Quiz / Testler</h3>
                <button
                  onClick={createQuiz}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={18} />
                  Yeni Quiz
                </button>
              </div>

              {loadingQuizzes ? (
                <p className="text-gray-500 text-center py-4">Yükleniyor...</p>
              ) : quizzes.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <FileText size={40} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">Henüz quiz eklenmemiş</p>
                  <p className="text-sm text-gray-400">Çoktan seçmeli, doğru/yanlış veya kısa cevaplı sorular ekleyebilirsiniz</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {quizzes.map(quiz => (
                    <div key={quiz.id} className="flex items-center justify-between p-3 border rounded-lg hover:border-blue-300">
                      <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => { setCurrentQuiz(quiz); setShowQuizEditor(true); }}>
                        <span className="text-2xl">📝</span>
                        <div>
                          <p className="font-medium text-gray-900">{quiz.title}</p>
                          <p className="text-xs text-gray-500">
                            {quiz.questionCount} soru • {quiz.totalPoints} puan
                            {quiz.isPublished ? ' • ✅ Yayında' : ' • 📝 Taslak'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setCurrentQuiz(quiz); setShowQuizEditor(true); }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => deleteQuiz(quiz.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quiz Editor Modal */}
              {showQuizEditor && currentQuiz && (
                <QuizEditorModal
                  quiz={currentQuiz}
                  onClose={() => { setShowQuizEditor(false); setCurrentQuiz(null); }}
                  onUpdate={loadQuizzes}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-between">
          <button
            onClick={onDelete}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
          >
            <Trash2 size={18} />
            Dersi Sil
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Kapat
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save size={18} />
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quiz Editor Modal Bileşeni
const QuizEditorModal = ({ quiz, onClose, onUpdate }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [quizSettings, setQuizSettings] = useState({
    title: quiz.title,
    description: quiz.description || '',
    duration: quiz.duration || '',
    passingScore: quiz.passingScore || 60,
    shuffleQuestions: quiz.shuffleQuestions || false,
    showAnswersAfter: quiz.showAnswersAfter !== false,
    maxAttempts: quiz.maxAttempts || 0
  });

  useEffect(() => {
    loadQuestions();
  }, [quiz.id]);

  const loadQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/quizzes/${quiz.id}/questions`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setQuestions(data);
      }
    } catch (error) {
      console.error('Sorular yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveQuizSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/quizzes/${quiz.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...quizSettings,
            lessonId: quiz.lessonId,
            courseId: quiz.courseId
          })
        }
      );
      alert('Quiz ayarları kaydedildi!');
      onUpdate();
    } catch (error) {
      console.error('Ayarlar kaydedilemedi:', error);
    }
  };

  const publishQuiz = async () => {
    if (questions.length === 0) {
      alert('Quiz\'de en az 1 soru olmalı');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/quizzes/${quiz.id}/publish`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      alert('Quiz yayınlandı!');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Quiz yayınlanamadı:', error);
    }
  };

  const addQuestion = async (questionData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/quizzes/${quiz.id}/questions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(questionData)
        }
      );
      if (response.ok) {
        loadQuestions();
        onUpdate();
        setShowAddQuestion(false);
      }
    } catch (error) {
      console.error('Soru eklenemedi:', error);
    }
  };

  const updateQuestion = async (questionId, questionData) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/questions/${questionId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(questionData)
        }
      );
      loadQuestions();
      onUpdate();
      setEditingQuestion(null);
    } catch (error) {
      console.error('Soru güncellenemedi:', error);
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Bu soruyu silmek istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/questions/${questionId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      loadQuestions();
      onUpdate();
    } catch (error) {
      console.error('Soru silinemedi:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-blue-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">📝 {quiz.title}</h2>
            <p className="text-sm text-gray-500">
              {questions.length} soru • {questions.reduce((sum, q) => sum + (q.points || 10), 0)} puan
              {quiz.isPublished ? ' • ✅ Yayında' : ' • 📝 Taslak'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Quiz Settings */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-3">Quiz Ayarları</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Quiz Başlığı</label>
                <input
                  type="text"
                  value={quizSettings.title}
                  onChange={(e) => setQuizSettings({...quizSettings, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Süre (dakika)</label>
                <input
                  type="number"
                  value={quizSettings.duration}
                  onChange={(e) => setQuizSettings({...quizSettings, duration: parseInt(e.target.value) || ''})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Sınırsız için boş bırakın"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Geçme Notu (%)</label>
                <input
                  type="number"
                  value={quizSettings.passingScore}
                  onChange={(e) => setQuizSettings({...quizSettings, passingScore: parseInt(e.target.value) || 60})}
                  className="w-full px-3 py-2 border rounded-lg"
                  min={0}
                  max={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Deneme Sayısı</label>
                <input
                  type="number"
                  value={quizSettings.maxAttempts}
                  onChange={(e) => setQuizSettings({...quizSettings, maxAttempts: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="0 = Sınırsız"
                  min={0}
                />
              </div>
            </div>
            <div className="flex gap-4 mt-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={quizSettings.shuffleQuestions}
                  onChange={(e) => setQuizSettings({...quizSettings, shuffleQuestions: e.target.checked})}
                />
                <span className="text-sm">Soruları karıştır</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={quizSettings.showAnswersAfter}
                  onChange={(e) => setQuizSettings({...quizSettings, showAnswersAfter: e.target.checked})}
                />
                <span className="text-sm">Sonunda cevapları göster</span>
              </label>
            </div>
            <button
              onClick={saveQuizSettings}
              className="mt-3 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
            >
              Ayarları Kaydet
            </button>
          </div>

          {/* Questions List */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Sorular</h3>
            <button
              onClick={() => setShowAddQuestion(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Plus size={18} />
              Soru Ekle
            </button>
          </div>

          {loading ? (
            <p className="text-center py-4 text-gray-500">Yükleniyor...</p>
          ) : questions.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">Henüz soru eklenmemiş</p>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map((question, index) => (
                <div key={question.id} className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          Soru {index + 1}
                        </span>
                        <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                          {question.questionType}
                        </span>
                        <span className="text-xs text-gray-500">{question.points} puan</span>
                      </div>
                      <p className="font-medium text-gray-900">{question.questionText}</p>
                      {question.options && question.options.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {question.options.map((opt, i) => (
                            <div key={opt.id} className={`text-sm ${opt.isCorrect ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
                              {String.fromCharCode(65 + i)}) {opt.text} {opt.isCorrect && '✓'}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => setEditingQuestion(question)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => deleteQuestion(question.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-between">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Kapat
          </button>
          <div className="flex gap-3">
            {quiz.isPublished && (
              <>
                <a
                  href={`/quiz/${quiz.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  📝 Quiz'i Çöz
                </a>
                <a
                  href={`/quiz/${quiz.id}/reports`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  📊 Raporlar
                </a>
              </>
            )}
            {!quiz.isPublished && (
              <button
                onClick={publishQuiz}
                disabled={questions.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                🚀 Yayınla
              </button>
            )}
          </div>
        </div>

        {/* Add Question Modal */}
        {(showAddQuestion || editingQuestion) && (
          <QuestionEditorModal
            question={editingQuestion}
            onSave={(data) => editingQuestion ? updateQuestion(editingQuestion.id, data) : addQuestion(data)}
            onClose={() => { setShowAddQuestion(false); setEditingQuestion(null); }}
          />
        )}
      </div>
    </div>
  );
};

// Question Editor Modal
const QuestionEditorModal = ({ question, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    questionText: question?.questionText || '',
    questionType: question?.questionType || 'Çoktan Seçmeli',
    options: question?.options || [
      { id: '1', text: '', isCorrect: false },
      { id: '2', text: '', isCorrect: false },
      { id: '3', text: '', isCorrect: false },
      { id: '4', text: '', isCorrect: false }
    ],
    correctAnswer: question?.correctAnswer || '',
    points: question?.points || 10,
    explanation: question?.explanation || ''
  });

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    if (field === 'isCorrect') {
      // Only one correct answer for multiple choice
      newOptions.forEach((opt, i) => {
        opt.isCorrect = i === index;
      });
    } else {
      newOptions[index][field] = value;
    }
    setFormData({ ...formData, options: newOptions });
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { id: String(Date.now()), text: '', isCorrect: false }]
    });
  };

  const removeOption = (index) => {
    if (formData.options.length <= 2) return;
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  const handleSubmit = () => {
    if (!formData.questionText.trim()) {
      alert('Soru metni zorunludur');
      return;
    }
    if (formData.questionType === 'Çoktan Seçmeli' || formData.questionType === 'Doğru/Yanlış') {
      if (!formData.options.some(o => o.isCorrect)) {
        alert('Doğru cevabı işaretleyin');
        return;
      }
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-xl font-bold mb-4">{question ? 'Soru Düzenle' : 'Yeni Soru Ekle'}</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Soru Tipi</label>
            <select
              value={formData.questionType}
              onChange={(e) => setFormData({ ...formData, questionType: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="Çoktan Seçmeli">Çoktan Seçmeli</option>
              <option value="Doğru/Yanlış">Doğru/Yanlış</option>
              <option value="Kısa Cevap">Kısa Cevap</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Soru Metni *</label>
            <textarea
              value={formData.questionText}
              onChange={(e) => setFormData({ ...formData, questionText: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              rows={3}
              placeholder="Soruyu buraya yazın..."
            />
          </div>

          {(formData.questionType === 'Çoktan Seçmeli' || formData.questionType === 'Doğru/Yanlış') && (
            <div>
              <label className="block text-sm font-medium mb-2">Seçenekler</label>
              <div className="space-y-2">
                {formData.options.map((option, index) => (
                  <div key={option.id} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={option.isCorrect}
                      onChange={() => handleOptionChange(index, 'isCorrect', true)}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm font-medium w-6">{String.fromCharCode(65 + index)})</span>
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg"
                      placeholder={`Seçenek ${String.fromCharCode(65 + index)}`}
                    />
                    {formData.options.length > 2 && (
                      <button
                        onClick={() => removeOption(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={addOption}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                + Seçenek Ekle
              </button>
            </div>
          )}

          {formData.questionType === 'Kısa Cevap' && (
            <div>
              <label className="block text-sm font-medium mb-1">Beklenen Cevap</label>
              <input
                type="text"
                value={formData.correctAnswer}
                onChange={(e) => setFormData({ ...formData, correctAnswer: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Doğru cevap..."
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Puan</label>
              <input
                type="number"
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 10 })}
                className="w-full px-3 py-2 border rounded-lg"
                min={1}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Açıklama (Opsiyonel)</label>
            <textarea
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              rows={2}
              placeholder="Cevap sonrası gösterilecek açıklama..."
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">
            İptal
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {question ? 'Güncelle' : 'Ekle'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Materyaller Sekmesi
const MaterialsTab = ({ courseId }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    if (courseId && courseId !== 'new') {
      loadMaterials();
    } else {
      setLoading(false);
    }
  }, [courseId]);

  const loadMaterials = async () => {
    try {
      const token = localStorage.getItem('token');
      // Bu endpoint backend'de mevcut değil, şimdilik boş bırakıyoruz
      // İleride kursa bağlı materyalleri getirmek için kullanılabilir
      setMaterials([]);
    } catch (error) {
      console.error('Materyaller yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadMaterials = async () => {
    if (selectedFiles.length === 0) {
      alert('Lütfen dosya seçin');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadedMaterials = [];

      for (let file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentVersionId', courseId); // Kurs ID'sini version ID gibi kullanıyoruz

        const uploadResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/library/upload`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          }
        );

        if (uploadResponse.ok) {
          const uploadedFile = await uploadResponse.json();
          uploadedMaterials.push(uploadedFile);
        } else {
          throw new Error(`${file.name} yüklenemedi`);
        }
      }

      alert(`${uploadedMaterials.length} dosya başarıyla yüklendi!`);
      setSelectedFiles([]);
      loadMaterials();
    } catch (error) {
      console.error('Dosya yükleme hatası:', error);
      alert('Dosyalar yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['ppt', 'pptx'].includes(ext)) return '📊';
    if (['mp4', 'avi', 'mov'].includes(ext)) return '🎬';
    if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼️';
    return '📎';
  };

  if (courseId === 'new') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Önce kursu kaydedin, sonra materyal ekleyebilirsiniz.</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold mb-6">Kurs Materyalleri</h3>

      {/* Dosya Yükleme Alanı */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6 hover:border-blue-400 transition">
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="material-upload"
          accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.jpg,.jpeg,.png"
        />
        <label htmlFor="material-upload" className="cursor-pointer">
          <div className="flex flex-col items-center gap-3">
            <Upload className="text-gray-400" size={48} />
            <span className="text-gray-600 font-medium">Materyal yüklemek için tıklayın</span>
            <span className="text-sm text-gray-500">PDF, Word, PowerPoint, Video, Görsel</span>
          </div>
        </label>
      </div>

      {/* Seçili Dosyalar */}
      {selectedFiles.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold mb-3">Seçili Dosyalar ({selectedFiles.length})</h4>
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getFileIcon(file.name)}</span>
                  <div>
                    <div className="font-medium">{file.name}</div>
                    <div className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  disabled={uploading}
                  className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={uploadMaterials}
            disabled={uploading}
            className="mt-4 w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold"
          >
            {uploading ? '📤 Yükleniyor...' : 'Tüm Dosyaları Yükle'}
          </button>
        </div>
      )}

      {/* Yüklenmiş Materyaller Listesi */}
      <div>
        <h4 className="font-semibold mb-3">Yüklenmiş Materyaller</h4>
        {materials.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-600">Henüz materyal yüklenmemiş</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {materials.map((material) => (
              <div key={material.id} className="border rounded-lg p-4 hover:shadow-md transition">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{getFileIcon(material.fileName)}</span>
                  <div className="flex-1">
                    <h5 className="font-medium">{material.originalFileName || material.fileName}</h5>
                    <p className="text-sm text-gray-500">{(material.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Canlı Dersler Sekmesi
const LiveSessionsTab = ({ courseId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [newSession, setNewSession] = useState({
    title: '',
    description: '',
    platform: 'zoom',
    meetingLink: '',
    scheduledAt: '',
    duration: 60
  });

  useEffect(() => {
    if (courseId && courseId !== 'new') {
      loadSessions();
    } else {
      setLoading(false);
    }
  }, [courseId]);

  const loadSessions = async () => {
    try {
      // Backend endpoint henüz yok, şimdilik boş bırakıyoruz
      setSessions([]);
    } catch (error) {
      console.error('Canlı dersler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSession = async () => {
    if (!newSession.title.trim() || !newSession.meetingLink.trim()) {
      alert('Ders adı ve toplantı linki zorunludur');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Backend endpoint oluşturulunca buraya eklenecek
      alert('Canlı ders oluşturuldu! (Backend entegrasyonu yapılacak)');
      setShowSessionModal(false);
      setNewSession({
        title: '',
        description: '',
        platform: 'zoom',
        meetingLink: '',
        scheduledAt: '',
        duration: 60
      });
      loadSessions();
    } catch (error) {
      console.error('Canlı ders oluşturma hatası:', error);
      alert('Canlı ders oluşturulurken hata oluştu');
    }
  };

  if (courseId === 'new') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Önce kursu kaydedin, sonra canlı ders ekleyebilirsiniz.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Canlı Dersler</h3>
        <button
          onClick={() => setShowSessionModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          <Plus size={18} />
          Yeni Canlı Ders
        </button>
      </div>

      {sessions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">Henüz canlı ders eklenmemiş.</p>
          <p className="text-sm text-gray-500 mt-2">Zoom veya Google Meet üzerinden canlı eğitimler planlayın.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sessions.map((session) => (
            <div key={session.id} className="border rounded-lg p-4 hover:shadow-md transition">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold">{session.title}</h4>
                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">CANLI</span>
              </div>
              {session.description && <p className="text-sm text-gray-600 mb-3">{session.description}</p>}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Video size={16} />
                <span>{session.platform === 'zoom' ? 'Zoom' : 'Google Meet'}</span>
              </div>
              {session.scheduledAt && (
                <div className="mt-2 text-sm text-gray-600">
                  📅 {new Date(session.scheduledAt).toLocaleString('tr-TR')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Canlı Ders Ekleme Modal */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Yeni Canlı Ders Planla</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Ders Başlığı *</label>
                <input
                  type="text"
                  value={newSession.title}
                  onChange={(e) => setNewSession({...newSession, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Örn: Canlı Q&A Oturumu - 1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Platform</label>
                <select
                  value={newSession.platform}
                  onChange={(e) => setNewSession({...newSession, platform: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="zoom">Zoom</option>
                  <option value="meet">Google Meet</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Toplantı Linki *</label>
                <input
                  type="url"
                  value={newSession.meetingLink}
                  onChange={(e) => setNewSession({...newSession, meetingLink: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://zoom.us/j/... veya meet.google.com/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tarih & Saat</label>
                <input
                  type="datetime-local"
                  value={newSession.scheduledAt}
                  onChange={(e) => setNewSession({...newSession, scheduledAt: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Süre (dakika)</label>
                <input
                  type="number"
                  value={newSession.duration}
                  onChange={(e) => setNewSession({...newSession, duration: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="15"
                  step="15"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Açıklama</label>
                <textarea
                  value={newSession.description}
                  onChange={(e) => setNewSession({...newSession, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowSessionModal(false); }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={createSession}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Planla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Yayınlama Sekmesi
const PublishTab = ({ formData, setFormData, course }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Görünürlük
      </label>
      <div className="space-y-3">
        <label className="flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <input
            type="radio"
            checked={formData.visibilityScope === 'Public'}
            onChange={() => setFormData({...formData, visibilityScope: 'Public'})}
            className="text-blue-600"
          />
          <div>
            <div className="font-medium">Tüm Okullar</div>
            <div className="text-sm text-gray-600">Kurs tüm organizasyonlara açık olacak</div>
          </div>
        </label>
        
        <label className="flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <input
            type="radio"
            checked={formData.visibilityScope === 'Restricted'}
            onChange={() => setFormData({...formData, visibilityScope: 'Restricted'})}
            className="text-blue-600"
          />
          <div>
            <div className="font-medium">Belirli Okullar</div>
            <div className="text-sm text-gray-600">Sadece seçili organizasyonlar erişebilir</div>
          </div>
        </label>
      </div>
    </div>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <p className="text-sm text-blue-800">
        <strong>Durum:</strong> {course?.status || 'Taslak'}
      </p>
      {course?.status === 'Taslak' && (
        <p className="text-sm text-blue-700 mt-2">
          Kurs şu anda taslak durumunda. Yayına almak için sağ üstteki "Yayına Al" butonunu kullanın.
        </p>
      )}
    </div>
  </div>
);

export default YomevCourseEditor;
